import { centerCalculator, GestCard, ICity, iconColumnSettings, ICoordinates, IMarker, ISharedConfig, isMarkerInsidePolygon, setDialog, setSnackbar } from '@shared/library';
import openGestConfirmationDialog from '@shared/library/dist/components/gest/GestDialog/GestConfirmationDialog/GestConfirmationDialog';
import { languages } from '@shared/library/dist/static/language';
import axios, { AxiosError } from 'axios';
import React from 'react';
import { read, utils } from 'xlsx';
import config from '../../../../config';
import importer from '../../../../helpers/importer';
import localProps from '../../../../helpers/localProps';
import { composeName } from '../methods';
import { interventionColumns, planColumns, WFMControllerName, WFMLeftGrid, WFMRightGrid } from '../static';
import './Planning.scss';
//#region ICONS
import { AssignmentTurnedIn, Download, FormatListBulletedAdd, Person, RotateLeft } from '@mui/icons-material';
import PlaylistAddRoundedIcon from '@mui/icons-material/AddCircle';
import SubstitutionIcon from '@mui/icons-material/AutoMode';
import DeleteRoundedIcon from '@mui/icons-material/Cancel';
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded';
import RemovalIcon from '@mui/icons-material/Clear';
import RepairIcon from '@mui/icons-material/Construction';
import MoveIcon from '@mui/icons-material/ContentPasteGo';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteRounded';
import DriveFolderUploadRoundedIcon from '@mui/icons-material/DriveFolderUploadRounded';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import FilterIcon from '@mui/icons-material/FilterAlt';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import PolygonIcon from '@mui/icons-material/Pentagon';
import ReadIcon from '@mui/icons-material/Pin';
import GeoIcon from '@mui/icons-material/Room';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import InstallIcon from '@mui/icons-material/Sensors';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
//#endregion
//#region COMPONENTS 
import GestDataGridPro from '@shared/library/dist/components/gest/GestDataGrid/GestDataGridPro';
import GestButton from '@shared/library/dist/components/gest/GestInputs/button/GestButton';
import GestSelect from '@shared/library/dist/components/gest/GestInputs/select/GestSelect';
import GestSwitch from '@shared/library/dist/components/gest/GestInputs/switch/GestSwitch';
import GestTextArea from '@shared/library/dist/components/gest/GestInputs/text area/GestTextArea';
import GestLoading from '@shared/library/dist/components/gest/GestLoading/GestLoading';
import GestMap from '@shared/library/dist/components/gest/GestMap/GestMap';
import Title from '@shared/library/dist/components/gest/Title/Title';
import {
    CustomButton,
    CustomGroup,
    EasyDropZone,
} from '../../../_gest';
import ChangeAssignedPlanForm from './ChangeAssignedPlanForm/ChangeAssignedPlanForm';
import CreatePlanForm from './CreatPlanForm/CreatePlanForm';
import CreateImportListForm from './CreateImportListForm/CreateImportListForm';
//#endregion
//#region MODELS
import { GridColDef, GridRowId } from '@mui/x-data-grid';
import { IGestButtonProps } from '@shared/library/dist/components/gest/GestInputs/button/GestButtonProps';
import { IGestSelectOption } from '@shared/library/dist/components/gest/GestInputs/select/GestSelectProps';
import { getMetersAPI, IGetMetersParams, IMeter, metersColumns, setAndStoreSelectedCity } from '@webgest/library';
import { IIntervention, Intervention, TInterventionType, TSubMode, TViewMode } from '../_models/Intervention';
import { IList } from '../_models/List';
import { IPlan } from '../_models/Plan';
//#endregion

export const excludedWfmPlanningColumns: (keyof IIntervention)[] = [
    "annotations",
    "anomaly",
    "anomaly_notes",
    "intervention_end_date",
    "intervention_start_date",
    "old_reading",
    "old_reading_date",
    "new_brand",
    "new_reading",
    "new_serial",
    "new_location",
    "result",
    "status",
    "alt",
    "fromMode"
];

const Planning = (props: ISharedConfig) => {
    //#region STATIC DATA
    const lang = languages(props.settings?.lang);
    document.title = config.app.name;
    const maxEmptyPlanLenght = 1000;
    const maxPlanLength = 3_000;
    const hiddenColumns = {
        fromMode: false,
        selected: false,
    };
    const iconOptionStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: "5px" };
    const interventionTypes: IGestSelectOption<IIntervention>[] = [
        { value: 'installation', label: <div style={iconOptionStyle} className='fw-bold text-success'><InstallIcon />{lang.installation}</div> },
        { value: 'removal', label: <div style={iconOptionStyle} className='fw-bold text-danger'><RemovalIcon />{lang.removal}</div> },
        { value: 'repair', label: <div style={iconOptionStyle} className='fw-bold text-warning'><RepairIcon />{lang.repair}</div> },
        { value: 'replacement', label: <div style={iconOptionStyle} className='fw-bold text-warning'><SubstitutionIcon />{lang.replacement}</div> },
        { value: 'reading', label: <div style={iconOptionStyle} className='fw-bold text-info'><ReadIcon />{lang.reading}</div> },
        { value: 'georeferencing', label: <div style={iconOptionStyle} className='fw-bold text-info'><GeoIcon />{lang.georeferencing}</div> },
    ];
    //#endregion

    //#region STATE
    //CITIES
    const [selectedAreaPolyline, setSelectedAreaPolyline] = React.useState<ICoordinates[][]>([]);

    //VIEW CHANGE
    const [viewMode, setViewMode] = React.useState<TViewMode>("list");
    const [subViewModes, setSubViewModes] = React.useState<TSubMode[]>(["grid"]);

    //METERS
    const [selectedInterventionType, setSelectedInterventionType] = React.useState<TInterventionType>(interventionTypes[3].value as TInterventionType);
    const [clickedMeterMarker, setClickedInstallMarker] = React.useState<IMeter>();
    const [meters, setMeters] = React.useState<IMeter[]>([]);

    //LISTS
    const [clickedListMarker, setClickedListMarker] = React.useState<IIntervention>();
    const [hiddenInterventions, setHiddenInterventions] = React.useState<("created" | "non-created")[]>(["created"]);
    const [lists, setLists] = React.useState<IList[]>([]);
    const [selectedList, setSelectedList] = React.useState<IList>();
    const [listDetails, setListDetails] = React.useState<IIntervention[]>([]);
    const [selectedFromPolygon, setSelectedFromPolygon] = React.useState<IIntervention[]>([]);

    const listsRef = React.useRef<IList[]>([]);
    const listInterventionsRef = React.useRef<IIntervention[]>([]);

    //PLAN EDIT
    const [existingPlans, setExistingPlans] = React.useState<IPlan[]>([]);
    const [selectedPlan, setSelectedPlan] = React.useState<IPlan>();

    const existingPlansRef = React.useRef<IPlan[]>([]);
    const selectedPlanRef = React.useRef<IPlan>(undefined);

    //PLAN DRAFT
    const [emptyPlanLength, setEmptyPlanLenght] = React.useState(maxEmptyPlanLenght);
    const [plan, setPlan] = React.useState<IIntervention[]>([]);
    const [planEdited, setPlanEdited] = React.useState<IIntervention[]>([]);

    const planRef = React.useRef<IIntervention[]>([]);
    //INTERVENTIONS
    const [selectedIntervention, setSelectedIntervention] = React.useState<IIntervention>();
    const [interventionsEdited, setInterventionsEdited] = React.useState<IIntervention[]>([]);
    //MAPS
    const [listInterventionsCenter, setListInterventionsCenter] = React.useState<ICoordinates>(config.env.map.defaultCenter);
    const [planCenter, setPlanCenter] = React.useState<ICoordinates>(config.env.map.defaultCenter);
    const [polygonCoords, setPolygonCoords] = React.useState<ICoordinates[]>([]);
    //FLAGS
    const [deleteListLoading, setDeleteListLoading] = React.useState(false);
    const [listsLoading, setListsLoading] = React.useState(false);
    const [listDetailsLoading, setListDetailsLoading] = React.useState(false);
    const [listDownloadLoading, setListDownloadLoading] = React.useState(false);
    const [emptyPlanCreateLoading, setEmptyPlanCreateLoading] = React.useState(false);
    const [listPreviewLoading, setListPreviewLoading] = React.useState(false);

    const [citiesLoading, setCitiesLoading] = React.useState(false);
    const [plansLoading, setPlansLoading] = React.useState(false);
    const [showDropzone, setShowDropzone] = React.useState(false);
    const [followPolygonCenter, setFollowPolygonCenter] = React.useState(localProps.panel.get('followPolygon') === 'true');
    //#endregion

    //#region METHODS
    //#region API CALL

    //#region GET
    const getCityMetersAPI = (city?: IGestSelectOption<ICity>) => {
        city ??= props.cache?.cities.selected;

        if (city?.value && city.value as number > 0) {
            const params: IGetMetersParams = {
                cityIDs: [city.value.toString()],
                withCommunicationTypes: true,
                withDeviceTypes: true,
                withBrands: true,
                withCoords: true
            };
            setCitiesLoading(true);

            return getMetersAPI(config.api.internal, params)
                .then(meters => setMeters(meters?.map<IMeter>((u, i) => ({ rowNumber: i + 1, ...u })) ?? []))
                .finally(() => setCitiesLoading(false));
        }
    };

    const getPlansAPI = () => {
        setPlansLoading(true);
        return axios.get<IPlan[]>(`${config.api.internal}/${WFMControllerName}/Plans`, { withCredentials: true, params: { archived: false, withFullInterventions: true } })
            .then(res => {
                existingPlansRef.current = res.data;
                setExistingPlans(existingPlansRef.current);

                selectedPlanRef.current = existingPlansRef.current[0];
                setSelectedPlan(selectedPlanRef.current);
            })
            .catch((err: AxiosError) => setSnackbar(props, { message: err.response?.data as string, severity: 'error' }))
            .finally(() => setPlansLoading(false));
    };

    const getLastFreePlanAPI = () =>
        axios.get<{ planID: number; interventionID: number; }>(`${config.api.internal}/WFM/Plan/LastFree`, { withCredentials: true })
            .then(res => res.data)
            .catch((err: AxiosError) => {
                console.error(err);
                return undefined;
            });

    const getListsAPI = () => {
        setListsLoading(true);
        axios.get<IList[]>(`${config.api.internal}/WFM/Lists`, { withCredentials: true })
            .then(res => {
                listsRef.current = res.data;
                setLists(listsRef.current);

                if (listsRef.current.length) {
                    getListDetailsAPI(listsRef.current[0]);
                }
            })
            .catch((err: AxiosError) => {
                console.error(err);
                setSnackbar(props, { message: "Errore durante richiesta per le liste di interventi", severity: 'error' });
            })
            .finally(() => setListsLoading(false));
    };

    const getListDetailsAPI = (list: IList) => {
        setListDetailsLoading(true);
        return axios.get<IIntervention[]>(`${config.api.internal}/WFM/List/${list.id}`, { withCredentials: true })
            .then(res => updateSelectedList(list, res.data))
            .catch((err: AxiosError) => {
                console.error(err);
                setSnackbar(props, { message: `Errore durante richiesta per interventi lista ${list.name}`, severity: 'error' });
            })
            .finally(() => setListDetailsLoading(false));
    };

    const getListPreviewAPI = async (files: File[]) => {
        let file = files[0];
        setListPreviewLoading(true);

        if (file.name.endsWith(".xlsx")) {
            //read excel
            const buffer = await file.arrayBuffer();
            const workbook = read(buffer, { type: "array" });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            //parse to csv
            const csvData = utils.sheet_to_csv(worksheet, { blankrows: false, FS: ";" });
            file = new File([csvData], file.name.replace(".xlsx", ".csv"), { type: "text/csv" });
        }
        //upload
        const formData = new FormData();
        formData.append("file", file);

        return axios.post<IIntervention[]>(`${config.api.internal}/WFM/List/Preview`, formData, { withCredentials: true })
            .then(res => {
                const interventions = res.data;

                if (interventions.length) {
                    openCreateListImportModal(interventions);
                }
                else {
                    setSnackbar(props, { message: "Importata lista vuota", severity: 'error' });
                }
            })
            .catch((err: AxiosError) => {
                console.error(err);
                setSnackbar(props, { message: "Errore anteprima di importazione della lista", severity: 'error' });
            })
            .finally(() => setListPreviewLoading(false));
    };

    const downloadListExampleAPI = () => {
        setListDownloadLoading(true);
        axios.get(`${config.api.internal}/WFM/List/Example/Excel`, { withCredentials: true, responseType: "blob" })
            .then(res => {
                const url = URL.createObjectURL(new Blob([res.data]));

                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "ImportazioneInterventi.xlsx");
                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
            })
            .catch((err) => console.error('Errore durante la creazione del CSV:', err))
            .finally(() => setListDownloadLoading(false));
    };
    //#endregion

    //DELETE
    const deleteListAPI = (listID: number) => {
        setDeleteListLoading(true);
        return axios.delete(`${config.api.internal}/WFM/List/${listID}`, { withCredentials: true })
            .then(() => {
                //remove deleted
                listsRef.current = lists.filter(l => l.id !== listID);
                setLists(listsRef.current);

                if (selectedList?.id === listID) {
                    //update selected if deleted
                    getListDetailsAPI(listsRef.current[0]);
                }

                setSnackbar(props, { message: "Lista eliminata con successo", severity: 'success' });
                setDialog(props, undefined);
            })
            .catch((error: AxiosError) => {
                console.error(error);
                setSnackbar(props, { message: "Errore durante assegnazione degli interventi al piano selezionato", severity: 'error' });
            })
            .finally(() => setDeleteListLoading(false));
    };
    //#endregion

    const getCurrentPlan = () => isEditingDisabled() ? plan : planEdited;

    const isEditingDisabled = () => !interventionsEdited.length;

    const doesRowMatchPlan = (fromPlan: IIntervention, fromOthers: IIntervention) => fromPlan.type === fromOthers.type && fromPlan.ciu === fromOthers.ciu;

    const isContainedInPlan = (row: IIntervention | IMeter) => getCurrentPlan().some(i => doesRowMatchPlan(i, "codiceUtente" in row ? new Intervention(row, selectedInterventionType) : row));

    const isPlanFull = (updatedPlan?: IIntervention[]) => (updatedPlan ?? planRef.current).length > maxPlanLength;

    const resetPlanEditing = () => {
        setPlanEdited([]);
        setInterventionsEdited([]);
    };
    /**on plan's intervention field edit*/
    const editIntervention = (row: IIntervention, prop: keyof IIntervention, newValue: string | number | boolean | TInterventionType | Date) => {
        updateSelectedIntervention();
        const currPlan = getCurrentPlan();
        const foundIndex = currPlan.findIndex(i => doesRowMatchPlan(i, row));

        if (foundIndex >= 0 && (row as any)[prop] !== newValue) {
            let updatedPlan = structuredClone(currPlan);
            (updatedPlan[foundIndex] as any)[prop] = newValue;

            const isDuplicated = prop === "type" && isContainedInPlan(updatedPlan[foundIndex]);

            if (isDuplicated) {
                setSnackbar(props, { message: "Rimosso elemento duplicato", severity: 'warning' });
                updatedPlan = currPlan.filter(i => !doesRowMatchPlan(i, row));
                setPlan(updatedPlan);
            }
            else {
                setInterventionsEdited([...interventionsEdited.filter(i => !doesRowMatchPlan(i, updatedPlan[foundIndex])), updatedPlan[foundIndex]]);
            }

            setPlanEdited(updatedPlan);
        }
    };
    /**add on button click from marker click*/
    const addMeterFromMap = (fromMap: IMeter) => {
        if (!isPlanFull()) {
            const lastAdded = new Intervention(fromMap as IMeter, selectedInterventionType, viewMode);

            updatePlan([...getCurrentPlan(), lastAdded]);
            setSnackbar(props, { message: lang.successfully_added, severity: 'success' });
        }
    };
    /**on installation or plan marker window's button or plan grid button click*/
    const deleteFromPlan = (toRemove: IIntervention) => {
        const remaining = getCurrentPlan().filter(i => !doesRowMatchPlan(i, toRemove));
        const editedRemaining = interventionsEdited.filter(i => !doesRowMatchPlan(i, toRemove));

        updatePlan(remaining);
        setInterventionsEdited(editedRemaining);
        updateSelectedIntervention(undefined);
    };
    /**set plan, map center and grid selected rows*/
    const updateSelectedIntervention = (selected?: IIntervention) => {
        setSelectedIntervention(selected);

        if (selected && selected.lat && selected.lng) {
            setPlanCenter(selected as ICoordinates);
        }
    };
    /**on any table select change*/
    const updateSelection = (ids: GridRowId[], idProp: keyof IIntervention, source?: IIntervention[]) => {
        const selected = source
            ?.filter(i => ids.includes(i[idProp] as GridRowId))
            .slice(0, maxPlanLength - planRef.current.length) ?? [];
        const selectionBlocked = selected.some(i => i.intervention_id);

        if (selectionBlocked) {
            setSnackbar(props, { message: "Impossibile inserire dalla lista un intervento già creato. Spostarlo dalla sezione piani per riutilizzarlo", severity: "error" });
        }

        mergeToPlan(selected.filter(i => !i.intervention_id));
    };
    /**update plan by union from plan and selection */
    const mergeToPlan = (selected: IIntervention[]) => {
        const fromPlan = getCurrentPlan().filter(i =>
            i.fromMode !== viewMode ||
            //from installations
            (!i.plan_id && !i.list_id && (i.type !== selectedInterventionType || i.municipality !== props.cache?.cities.selected?.value)) ||
            //from curr list
            (i.list_id && i.list_id !== selectedList?.id) ||
            //from curr edit
            (i.plan_id && i.plan_id !== selectedPlan?.id) ||
            selected.some(si => doesRowMatchPlan(i, si))
        );
        const updatedPlan = [
            ...fromPlan,
            ...selected.filter(si => !fromPlan.some(i => doesRowMatchPlan(i, si)))
        ];

        if (!isPlanFull(updatedPlan)) {
            updatePlan(updatedPlan);
        }
        else {
            setSnackbar(props, { message: `Impossibile inserire più di ${maxPlanLength} interventi in un piano`, severity: "error" });
        }
    };
    /**set plan and map center*/
    const updatePlan = (updatedPlan: IIntervention[]) => {
        const ordered = updatedPlan.sort((a, b) => (a.ciu < b.ciu && -1) || (a.ciu > b.ciu && 1) || 0);
        setPlan(ordered);

        if (!isEditingDisabled()) {
            setPlanEdited(ordered);
        }

        if (updatedPlan.length) {
            setPlanCenter(updatedPlan[updatedPlan.length - 1] as ICoordinates);
        }
    };
    /**on edit plan table click*/
    const updateSelectedPlan = (plan: IPlan) => {
        if (plan.id !== selectedPlanRef.current?.id) {
            //set new selected
            selectedPlanRef.current = { ...plan };
            setSelectedPlan(selectedPlanRef.current);
        }
    };
    /**on list details table's select change*/
    const updateSelectedList = (list: IList, interventions?: IIntervention[]) => {
        //set new selected
        listInterventionsRef.current = interventions ?? list.interventions ?? [];
        setListDetails(listInterventionsRef.current);
        setSelectedList(list);

        const center = centerCalculator([listInterventionsRef.current as ICoordinates[]]);

        if (center) {
            setListInterventionsCenter(center);
        }
    };
    /**on map remove button's click*/
    const removeInPolygonFromPlan = () => {
        const fromPolygon = selectedFromPolygon.filter(sip => isContainedInPlan(sip));
        const updatedPlan = getCurrentPlan().filter(i => !fromPolygon.some(sip => doesRowMatchPlan(sip, i)));

        updatePlan(updatedPlan);
    };
    /**on list map submit polygon button's double click*/
    const resetSelectionFromPolygon = () => {
        setPolygonCoords([]);
        setSelectedFromPolygon([]);
    };
    /**on list map CTRL + click*/
    const updateListSelectionFromPolygon = (event: google.maps.MapMouseEvent) => {
        //clicked
        event.stop();

        if ((event.domEvent as KeyboardEvent).ctrlKey && event.latLng) {
            const coords = [...polygonCoords, { lat: event.latLng.lat(), lng: event.latLng.lng() }];
            const selected = listDetails
                .filter(i =>
                    i.lng &&
                    i.lat &&
                    !i.intervention_id &&
                    isMarkerInsidePolygon(i as ICoordinates, coords)
                )
                .map<IIntervention>(i => ({
                    ...i,
                    fromMode: viewMode,
                    listName: composeName(selectedList)
                }));

            setSelectedFromPolygon(selected);
            setPolygonCoords(coords);

            const center = centerCalculator([coords]);

            if (center) {
                setListInterventionsCenter(center);
            }
        }
    };

    const updateMeterSelectionFromPolygon = (event: google.maps.MapMouseEvent) => {
        //clicked
        event.stop();

        if ((event.domEvent as KeyboardEvent).ctrlKey && event.latLng) {
            const coords = [...polygonCoords, { lat: event.latLng.lat(), lng: event.latLng.lng() }];
            const selected = meters
                .filter(i =>
                    i.lng &&
                    i.lat &&
                    isMarkerInsidePolygon(i as ICoordinates, coords)
                )
                .map<IIntervention>(i => new Intervention(i, selectedInterventionType, viewMode));

            setSelectedFromPolygon(selected);
            setPolygonCoords(coords);

            const center = centerCalculator([coords]);

            if (center) {
                setListInterventionsCenter(center);
            }
        }
    };
    /**on map add button's click*/
    const submitListSelectionFromPolygon = () => {
        if (!isPlanFull()) {
            const fromGrid = listDetails
                .filter(i =>
                    i.lng &&
                    i.lat &&
                    !i.intervention_id &&
                    isContainedInPlan(i) &&
                    !selectedFromPolygon
                        .filter(sip => !isContainedInPlan(sip))
                        .some(sip => sip.ciu === i.ciu)
                )
                .map<IIntervention>(i => ({
                    ...i,
                    fromMode: viewMode,
                    listName: composeName(selectedList)
                }));

            mergeToPlan([...fromGrid, ...selectedFromPolygon]);
        }
    };

    const submitMeterSelectionFromPolygon = () => {
        if (!isPlanFull()) {
            const fromGrid = meters
                .filter(u =>
                    u.lng &&
                    u.lat &&
                    isContainedInPlan(u) &&
                    !selectedFromPolygon
                        .filter(sip => !isContainedInPlan(sip))
                        .some(sip => sip.ciu.toString() === u.codiceUtente.toString())
                )
                .map<IIntervention>(i => new Intervention(i, selectedInterventionType, viewMode));

            mergeToPlan([...fromGrid, ...selectedFromPolygon]);
        }
    };
    /**change list's markers visibility*/
    const updateHiddenInterventionsFromList = (checked: boolean, area: "created" | "non-created") => {
        let hiddens: ("created" | "non-created")[] = [];

        if (!(hiddenInterventions.includes("created") && hiddenInterventions.includes("created"))) {
            hiddens = !checked ? [...hiddenInterventions, area] : hiddenInterventions.filter(fi => fi !== area);
        }

        setHiddenInterventions(hiddens);
    };
    /**pick icon for list marker*/
    const renderMarkerIcon = (i: IIntervention | IMeter, clicked?: IIntervention | IMeter) => {
        let icon = importer.ic.require('markerBlue.png');
        const currID = "ciu" in i ? i.ciu : i.codiceUtente;
        const clickedID = clicked ? "ciu" in clicked ? clicked?.ciu : clicked?.codiceUtente : undefined;

        if (clickedID && clickedID === currID) {
            icon = importer.ic.require('markerSelected.svg');
        }
        else if (isContainedInPlan(i)) {
            //in current plan
            icon = importer.ic.require('markerGreen.png');
        }
        else if (selectedFromPolygon.some(sip => sip.ciu === currID)) {
            //selected from polygon drawing
            icon = importer.ic.require('markerBanana.png');
        }
        else if ("intervention_id" in i && i.intervention_id) {
            //imported from list in existent plan
            icon = importer.ic.require('markerRed.png');
        }

        return icon;
    };

    //#region MODALS
    const openCreateModal = async (emptyPlan?: boolean) => {
        let currPlan = { name: "", interventions: structuredClone(plan) } as IPlan;

        if (emptyPlan) {
            setEmptyPlanCreateLoading(true);

            const idSum = 1111122330000000;
            const lastPlan = await getLastFreePlanAPI();
            const startIndex = lastPlan?.interventionID ?? 0;

            currPlan.name = `Free ${(lastPlan?.planID ?? 0) + 1}`;
            currPlan.interventions = [];

            for (let i = 0, j = startIndex; i < emptyPlanLength; i++) {
                j++;
                currPlan.interventions[i] = {
                    id: j,
                    ciu: idSum + j,
                    type: "replacement",
                    status: "unprocessed"
                } as IIntervention;
            }
            setPlan(currPlan.interventions);
            resetPlanEditing();

            setEmptyPlanCreateLoading(false);
        }

        setDialog(props, {
            label: lang.preview,
            content:
                <CreatePlanForm {...{ lang, existingPlans, hiddenColumns }}
                    config={props}
                    cities={props.cache?.cities.options ?? []}
                    plan={currPlan}
                    onSuccess={() => {
                        getPlansAPI();
                        setPlan([]);
                        resetPlanEditing();

                        if (selectedList) {
                            getListDetailsAPI(selectedList);
                        }
                    }}
                />
        });
    };

    const openAssignmentModal = () => setDialog(props, {
        label: lang.edit,
        content:
            <ChangeAssignedPlanForm
                {...{ lang, existingPlans, hiddenColumns, plan }}
                config={props}
                onSuccess={() => {
                    getPlansAPI();
                    setPlan([]);

                    if (selectedList) {
                        getListDetailsAPI(selectedList);
                    }
                }}
            />
    });

    const openCreateListImportModal = (interventions: IIntervention[]) => setDialog(props, {
        label: lang.preview,
        h: "70vh",
        content:
            <CreateImportListForm
                {...{ lang, lists, hiddenColumns, interventions }}
                config={props}
                onSuccess={getListsAPI}
                cities={props.cache?.cities.options ?? []}
            />
    });

    const openDeleteListModal = async (id: number) => await openGestConfirmationDialog(modal => setDialog(props, modal), lang, undefined, true) && deleteListAPI(id);
    //#endregion
    //#endregion

    //#region STATE CHANGE
    React.useEffect(() => {
        getListsAPI();
        getPlansAPI();
    }, []);

    React.useEffect(() => {
        if (props.cache?.cities.options?.length && (props.cache.cities.selected?.value ?? -1) === -1) {
            setAndStoreSelectedCity(props, props.cache!.cities.options[0]);
        }
    }, [props.cache?.cities]);

    React.useEffect(() => {
        if (props.cache?.cities.selected?.value) {
            getCityMetersAPI();
        }
    }, [props.cache?.cities.selected]);

    React.useEffect(() => {
        planRef.current = getCurrentPlan();

        if (isPlanFull(planRef.current)) {
            setSnackbar(props, { message: `Impossibile inserire più di ${maxPlanLength} interventi in un piano`, severity: "error" });
        }
    }, [plan, planEdited]);

    React.useEffect(resetSelectionFromPolygon, [viewMode]);

    React.useEffect(() => {
        const noCoords = !(viewMode === 'installations' ? meters : listDetails).some(i => i.lat && i.lng);

        if (noCoords) {
            setSubViewModes(["grid"]);
        }
    }, [meters, listDetails, viewMode]);
    //#endregion

    //#region RENDER
    const draftedInterventionColumns: GridColDef<IIntervention>[] = React.useMemo(() => [
        //hidden
        {
            field: 'fromMode',
            filterable: false,
            hideable: false
        },
        //visible
        {
            ...iconColumnSettings<IIntervention, void>(undefined, <DeleteRoundedIcon color='error' />, lang.remove, (params) => deleteFromPlan(params.row)),
            hideable: false
        },
        ...interventionColumns({
            lang
            , cities: props.cache?.cities.options
            , lists
            , plans: existingPlans
            , excluded: [...excludedWfmPlanningColumns, "intervention_id"]
            , editables: [
                { prop: "type", onchange: (row, newvalue) => editIntervention(row, "type", (newvalue as IGestSelectOption<IIntervention>[])[0].value as TInterventionType), options: { options: interventionTypes, required: true } },
                { prop: "supply_type", onchange: (row, newvalue) => editIntervention(row, "supply_type", newvalue as string) },
                { prop: "email", onchange: (row, newvalue) => editIntervention(row, "email", newvalue as string) },
                { prop: "telephone", onchange: (row, newvalue) => editIntervention(row, "telephone", newvalue as string) },
                { prop: "district", onchange: (row, newvalue) => editIntervention(row, "district", newvalue as string) },
                { prop: "old_location", onchange: (row, newvalue) => editIntervention(row, "old_location", newvalue as string) },
                { prop: "causal", onchange: (row, newvalue) => editIntervention(row, "causal", newvalue as string) },
                { prop: "notes", onchange: (row, newvalue) => editIntervention(row, "notes", newvalue as string) },
                { prop: "specifics", onchange: (row, newvalue) => editIntervention(row, "specifics", newvalue as string) },
            ]
        })

    ], [lang]);
    //#endregion

    return <div id="planning">
        <Title size="xl" class="title align-items-center justify-content-between">
            {lang.planning}

            <CustomGroup variant="union-shadowed">
                <CustomButton
                    variant="light"
                    title={`${lang.lists} ${lang.interventions}`}
                    active={viewMode === 'list'}
                    onClick={() => {
                        setClickedInstallMarker(undefined);
                        setViewMode('list');
                    }}
                >
                    <FormatListBulletedRoundedIcon />
                </CustomButton>

                <CustomButton variant="light"
                    title={lang.end_users}
                    active={viewMode === 'installations'}
                    onClick={() => {
                        setClickedListMarker(undefined);
                        setViewMode('installations');
                    }}
                >
                    <Person />
                </CustomButton>

                <CustomButton
                    variant="light"
                    title={`${lang.plans} ${lang.existents}`}
                    active={viewMode === 'existentPlans'}
                    onClick={() => setViewMode('existentPlans')}
                >
                    <AssignmentTurnedIn />
                </CustomButton>
            </CustomGroup>
        </Title>

        {/* DATA SOURCE */}
        <GestCard id="source"
            title={viewMode === 'installations' ? "Meters" : viewMode === 'list' ? lang.lists : lang.plans}
            onDragOver={e => {
                if (e.dataTransfer.types.includes('Files')) {
                    if (viewMode !== 'list') setViewMode('list');
                    if (!showDropzone) setShowDropzone(true);
                }
            }}
            actions={viewMode === "existentPlans" ||
                <div className='align-items-center'>
                    {viewMode === "installations"
                        ?
                        <>
                            <GestSelect
                                required
                                label={lang.municipality}
                                loading={props.cache?.cities.loading}
                                value={props.cache?.cities.selected?.value}
                                options={props.cache?.cities.options}
                                w={"12vw"}
                                onchange={selected => setAndStoreSelectedCity(props, selected[0])}
                            />

                            <GestSelect
                                required
                                label={lang.intervention_type}
                                disabled
                                w={140}
                                value={selectedInterventionType}
                                options={interventionTypes}
                                onchange={(value) => setSelectedInterventionType(value!.toString() as TInterventionType)}
                            />
                        </>
                        :
                        <>
                            <GestSelect
                                required
                                w={"18vw"}
                                disabled={deleteListLoading || listsLoading}
                                label={lang.lists}
                                value={selectedList?.id ?? ""}
                                options={lists.map<IGestSelectOption<IList>>(l => ({
                                    value: l.id,
                                    label: composeName(l),
                                    data: l,
                                    buttons: [{
                                        iconBtn: true,
                                        loading: deleteListLoading,
                                        tooltip: lang.delete,
                                        alignTooltip: "left",
                                        onclick: () => openDeleteListModal(l.id as number),
                                        icon: <DeleteForeverRoundedIcon color='error' />,
                                    }]
                                }))}
                                onchange={selected => selected[0].value !== selectedList?.id && getListDetailsAPI(selected[0].data!)}
                            />

                            {showDropzone &&
                                <GestButton
                                    iconBtn
                                    icon={<Download />}
                                    onclick={downloadListExampleAPI}
                                    tooltip={<>
                                        Scarica Excel di esempio. Colonne ammesse:<br />
                                        <br />
                                        CIU (OBBLIGATORIO, numero intero)<br />
                                        CodiceFornitura (numero intero)<br />
                                        CodiceAnagrafico (numero intero)<br />
                                        <br />
                                        LatitudineContatore (numero con virgola)<br />
                                        LongitudineContatore (numero con virgola)<br />
                                        <br />
                                        DataIntervento (data GG/MM/AAAA, max 10 caratteri)<br />
                                        <br />
                                        TelefonoIntestatario (max 20 caratteri)<br />
                                        <br />
                                        TipoIntervento (OBBLIGATORIO, max 50 caratteri)<br />
                                        TipoFornitura (max 50 caratteri)<br />
                                        MarcaContatore (max 50 caratteri)<br />
                                        PosizioneContatore (max 50 caratteri)<br />
                                        <br />
                                        ODI (max 64 caratteri)<br />
                                        <br />
                                        IntestatarioFornitura (max 255 caratteri)<br />
                                        ComuneFornitura (max 255 caratteri)<br />
                                        DistrettoFornitura (max 255 caratteri)<br />
                                        SerialeContatore (max 255 caratteri)<br />
                                        EmailIntestatario (max 255 caratteri)<br />
                                        <br />
                                        IndirizzoFornitura (max 1024 caratteri)
                                    </>}
                                />
                            }

                            <GestButton
                                iconBtn
                                icon={<DriveFolderUploadRoundedIcon />}
                                onclick={() => setShowDropzone(!showDropzone)}
                                tooltip={lang.import_list}
                                active={showDropzone}
                            />
                        </>
                    }

                    <CustomGroup
                        variant={'union'}
                        style={{ boxShadow: '1px solid rgba(var(--primary-rgb), 0.25)' }}
                    >
                        <CustomButton
                            variant="light"
                            active={subViewModes.includes("grid")}
                            title={lang.tabular}
                            onClick={() => {
                                const remaining = subViewModes.filter(svm => svm !== "grid");
                                const mode: TSubMode[] = subViewModes.includes('grid')
                                    ? remaining.length ? remaining : ["map"]
                                    : [...subViewModes, "grid"];

                                setSubViewModes(mode);
                            }}
                        >
                            <TableChartRoundedIcon />
                        </CustomButton>

                        <CustomButton
                            variant="light"
                            active={subViewModes.includes('map')}
                            title={lang.map}
                            disabled={!((viewMode === 'installations' ? meters : listDetails).some(i => i.lat && i.lng))}
                            onClick={() => {
                                const remaining = subViewModes.filter(svm => svm !== "map");
                                const mode: TSubMode[] = subViewModes.includes('map')
                                    ? remaining.length ? remaining : ["grid"]
                                    : [...subViewModes, "map"];

                                setSubViewModes(mode);
                            }}
                        >
                            <MapRoundedIcon />
                        </CustomButton>
                    </CustomGroup>
                </div>
            }
        >
            {(viewMode === 'installations' || viewMode === 'list')
                ?
                <>{/* INTERVENTIONS FROM INSTALLATIONS OR LISTS */}
                    {viewMode === "list" && showDropzone && (
                        listPreviewLoading
                            ? <GestLoading />
                            :
                            <EasyDropZone
                                accept=".csv, .xlsx"
                                onChange={getListPreviewAPI}
                                onError={(error: any) => console.error(error)}
                                ignoreInvalidFiles
                            />
                    )}

                    {subViewModes.includes('grid') &&
                        <GestDataGridPro
                            columns={(viewMode === "installations"
                                ? metersColumns(lang)
                                : interventionColumns({
                                    lang,
                                    cities: props.cache?.cities.options,
                                    plans: existingPlans,
                                    excluded: [...excludedWfmPlanningColumns, "plan_id", "list_id"]
                                })
                            ) as GridColDef<IIntervention | IMeter>[]}
                            rows={viewMode === "installations" ? meters : listDetails}

                            selectedRowIDs={(viewMode === "installations" ? meters : listDetails)
                                .filter(i => isContainedInPlan(i))
                                .map(u => Number("codiceUtente" in u ? u.codiceUtente : u.id))
                            }
                            updateSelection={ids => updateSelection(ids,
                                viewMode === "installations" ? "ciu" : "id",
                                viewMode === "installations"
                                    ? meters.map(u => new Intervention(u, selectedInterventionType, viewMode))
                                    : listDetails.map<IIntervention>(i => ({ ...i, fromMode: viewMode, listName: composeName(selectedList) }))
                            )}

                            loading={viewMode === "installations"
                                ? citiesLoading
                                : listDetailsLoading || deleteListLoading || listsLoading
                            }
                            columnVisibilityModel={{ idTipoUtenza: false }}
                            w={WFMLeftGrid}
                        />}

                    {subViewModes.includes('map') &&
                        <GestMap
                            lang={lang}
                            style={{ width: WFMLeftGrid }}
                            tips={[lang.press_ctrl_and_click_to_draw_selection_area]}

                            center={centerCalculator(viewMode === "installations" ? props.cache?.cities.selected?.data?.geoArea : [[listInterventionsCenter]])}
                            onClick={viewMode === "installations" ? updateMeterSelectionFromPolygon : updateListSelectionFromPolygon}
                            buttonsRowDirection={subViewModes.length > 1}
                            buttons={[
                                {
                                    icon: <PolygonIcon />,
                                    disabled: !polygonCoords.length,
                                    tooltip: `Reset ${lang.polygon}`,
                                    onclick: resetSelectionFromPolygon,
                                },
                                {
                                    icon: <CenterFocusStrongRoundedIcon />,
                                    disabled: !polygonCoords.length,
                                    tooltip: lang.follow_center,
                                    onclick: () => {
                                        const newValue = !followPolygonCenter;
                                        localProps.panel.set('followPolygon', newValue.toString());
                                        setFollowPolygonCenter(newValue);
                                    }
                                },
                                {
                                    icon: <PlaylistAddRoundedIcon />,
                                    disabled: !selectedFromPolygon.filter(i => !isContainedInPlan(i)).length,
                                    tooltip: `${lang.add_selected} (${selectedFromPolygon.filter(i => !isContainedInPlan(i)).length})`,
                                    onclick: viewMode === "installations" ? submitMeterSelectionFromPolygon : submitListSelectionFromPolygon
                                },
                                {
                                    icon: <DeleteRoundedIcon />,
                                    disabled: !selectedFromPolygon.filter(i => isContainedInPlan(i)).length,
                                    tooltip: `${lang.remove_selected} (${selectedFromPolygon.filter(i => isContainedInPlan(i)).length})`,
                                    onclick: removeInPolygonFromPlan
                                }
                            ]}
                            polygons={{
                                areas: viewMode === "installations" ? [{ points: props.cache?.cities.selected?.data?.geoArea }] : undefined,
                                draw: { coords: polygonCoords, set: setPolygonCoords }
                            }}
                            markerInfoWindow={{
                                set: viewMode === "installations" ? setClickedInstallMarker : setClickedListMarker,
                                marker: viewMode === "installations" ? clickedMeterMarker : clickedListMarker,
                                position: (viewMode === "installations" ? clickedMeterMarker : clickedListMarker) as ICoordinates | undefined,
                                windowActions: clickedMeterMarker || (clickedListMarker && !clickedListMarker.intervention_id)
                                    ? [isContainedInPlan(clickedMeterMarker ?? clickedListMarker!)
                                        ? {
                                            icon: <DeleteRoundedIcon color="error" />,
                                            tooltip: lang.remove,
                                            iconBtn: true,
                                            onclick: () => deleteFromPlan(clickedMeterMarker ? new Intervention(clickedMeterMarker, selectedInterventionType) : clickedListMarker!)
                                        }
                                        : clickedMeterMarker || !clickedListMarker?.intervention_id
                                            ? {
                                                iconBtn: true,
                                                icon: <PlaylistAddRoundedIcon color="success" />,
                                                tooltip: lang.add,
                                                onclick: () => clickedMeterMarker
                                                    ? addMeterFromMap(clickedMeterMarker)
                                                    : updatePlan([...getCurrentPlan(), { ...clickedListMarker!, fromMode: viewMode }])
                                            }
                                            : {} as IGestButtonProps
                                    ]
                                    : undefined
                            }}
                            markers={(viewMode === "installations"
                                ? meters
                                : listDetails.filter(i =>
                                    !hiddenInterventions.length ||
                                    (hiddenInterventions.includes("created") && !i.intervention_id) ||
                                    (hiddenInterventions.includes("non-created") && i.intervention_id)
                                ))
                                .map<IMarker<IMeter | IIntervention>>((i, j) => ({
                                    id: j,
                                    model: i,
                                    options: {
                                        position: i as ICoordinates,
                                        onClick: () => viewMode === "installations" ? setClickedInstallMarker(i as IMeter) : setClickedListMarker(i as IIntervention),
                                        icon: renderMarkerIcon(i, viewMode === "installations" ? clickedMeterMarker : clickedListMarker),
                                        zIndex: (
                                            (viewMode === "installations" && clickedMeterMarker?.codiceUtente === (i as IMeter).codiceUtente) ||
                                            clickedListMarker?.ciu === (i as IIntervention).ciu ||
                                            isContainedInPlan(i)
                                        )
                                            ? 2 : 1,
                                    }
                                }))
                            }
                        >
                            {viewMode === "list" &&
                                <div id='visibilityFilter' className='flex-column'>
                                    <strong className='align-items-center'><FilterIcon />{`${lang.edit} ${lang.visibility}`}</strong>

                                    <GestSwitch
                                        label={`In un piano: ${listDetails.filter(i => i.intervention_id).length}`}
                                        checked={!hiddenInterventions.includes("created")}
                                        onchange={(checked: boolean) => updateHiddenInterventionsFromList(checked, "created")}
                                    />

                                    <GestSwitch
                                        label={`Nessun piano: ${listDetails.filter(i => !i.intervention_id).length}`}
                                        checked={!hiddenInterventions.includes("non-created")}
                                        onchange={(checked: boolean) => updateHiddenInterventionsFromList(checked, "non-created")}
                                    />
                                </div>
                            }
                        </GestMap>
                    }
                </>
                : <>{/* INTERVENTIONS FROM EXISTENT PLAN */}
                    <GestDataGridPro
                        columns={planColumns({ lang, excluded: ["archived_at", "operators"] })}
                        rows={existingPlans}

                        selectedRowIDs={selectedPlan ? [selectedPlan.id as number] : []}
                        onRowClick={params => updateSelectedPlan(params.row as IPlan)}

                        loading={plansLoading}
                        columnVisibilityModel={hiddenColumns}
                        w={WFMLeftGrid}
                    />

                    <GestDataGridPro
                        columns={interventionColumns({
                            lang,
                            cities: props.cache?.cities.options,
                            plans: existingPlans,
                            excluded: ["plan_id", "intervention_id", "list_id"],
                        })}
                        rows={selectedPlan?.interventions ?? []}

                        selectedRowIDs={selectedPlan?.interventions?.filter(i => isContainedInPlan(i)).map(i => i.id!) ?? []}
                        updateSelection={ids => updateSelection(ids, "id", selectedPlan?.interventions?.map<IIntervention>(i => ({ ...i, fromMode: viewMode })))}

                        loading={plansLoading}
                        w={WFMLeftGrid}
                    />
                </>
            }
        </GestCard>

        {/* PLAN DRAFT */}
        <GestCard id="plan"
            title={lang.plan}
            actions={
                <form className='flex-row centered' onSubmit={e => { e.preventDefault(); openCreateModal(true); }}>
                    <GestTextArea
                        label={"Numero di interventi"}
                        type='number'
                        maxLen={maxEmptyPlanLenght}
                        value={emptyPlanLength}
                        onchange={newValue => setEmptyPlanLenght(Number(newValue))}
                    />

                    <GestButton
                        iconBtn
                        loading={emptyPlanCreateLoading}
                        tooltip={`Crea ${emptyPlanLength} interventi vuoti`}
                        icon={<FormatListBulletedAdd />}
                        submit
                    />
                </form>
            }
        >
            <GestDataGridPro
                columns={draftedInterventionColumns}
                rows={getCurrentPlan()}

                selectedRowIDs={selectedIntervention && isContainedInPlan(selectedIntervention) ? [selectedIntervention.id ?? selectedIntervention.ciu] : undefined}
                onRowClick={params => updateSelectedIntervention(selectedIntervention && doesRowMatchPlan(params.row as IIntervention, selectedIntervention) ? undefined : params.row)}

                rowHeight={33}
                toolbar={[{
                    iconBtn: true,
                    icon: <EditOffIcon />,
                    tooltip: `${lang.cancel} ${lang.edit}`,
                    disabled: isEditingDisabled(),
                    onclick: () => {
                        updateSelectedIntervention();
                        resetPlanEditing();
                    }
                },
                {
                    iconBtn: true,
                    icon: <EditIcon />,
                    tooltip: `${lang.save} ${lang.edit}`,
                    disabled: isEditingDisabled(),
                    onclick: () => {
                        setPlan(planEdited);
                        resetPlanEditing();
                        setSnackbar(props, { message: lang.successfully_updated, severity: 'success' });
                    }
                },
                {
                    iconBtn: true,
                    icon: <RotateLeft />,
                    tooltip: lang.empty_plan,
                    onclick: () => {
                        setPlan([]);
                        setPolygonCoords([]);
                        setSelectedFromPolygon([]);
                        updateSelectedIntervention();
                        resetPlanEditing();
                    }
                },
                {
                    iconBtn: true,
                    icon: <MoveIcon />,
                    tooltip: `${lang.add_move} ${lang.interventions} in ${lang.plan} ${lang.existent}`,
                    disabled: !isEditingDisabled() || !existingPlans.length,
                    onclick: openAssignmentModal,
                },
                {
                    iconBtn: true,
                    icon: <SaveRoundedIcon />,
                    tooltip: `${lang.create_new} ${lang.plan}`,
                    disabled: !isEditingDisabled(),
                    onclick: () => openCreateModal(),
                }]}
                columnVisibilityModel={{ fromMode: false }}
                w={WFMRightGrid}
            />
        </GestCard>

        {/* PLAN DRAFT MAP */}
        <GestCard id="map">
            <GestMap
                lang={lang}
                zoom={12}
                center={planCenter}
                polygons={{
                    areas: props.cache?.cities.options
                        .filter(c => c.data?.geoArea?.length && plan.some(i => i.municipality === c.value))
                        .flatMap(c => ({ points: c.data?.geoArea! }))
                }}
                markers={[
                    ...(viewMode === "existentPlans" && selectedPlan?.interventions ? selectedPlan.interventions : []).map<IMarker<IIntervention>>((i, j) => ({
                        id: i.id ?? j,
                        model: i,
                        options: {
                            position: i as ICoordinates,
                            zIndex: selectedIntervention?.ciu === i.ciu || isContainedInPlan(i) ? 2 : 1,
                            icon: importer.ic.require(selectedIntervention?.ciu === i.ciu ? 'markerSelected.svg' : 'markerDarkGreen.png')
                        }
                    })),
                    ...getCurrentPlan().map<IMarker<IIntervention>>((i, j) => ({
                        id: i.id ?? j,
                        model: i,
                        options: {
                            position: i as ICoordinates,
                            zIndex: selectedIntervention?.ciu === i.ciu || isContainedInPlan(i) ? 2 : 1,
                            icon: importer.ic.require(selectedIntervention?.ciu === i.ciu ? 'markerSelected.svg' : 'markerGreen.png')
                        }
                    }))
                ]}
                markerInfoWindow={{
                    title: selectedIntervention?.ciu,
                    set: updateSelectedIntervention,
                    marker: selectedIntervention,
                    windowActions: [{
                        ...(selectedIntervention && isContainedInPlan(selectedIntervention)
                            ? { label: lang.remove, onclick: () => deleteFromPlan(selectedIntervention) }
                            : undefined),
                        icon: <DeleteRoundedIcon color='error' />
                    }]
                }}
            />
        </GestCard>
    </div >;
};
export default React.memo(Planning);